import { usePageMeta } from '@/hooks/usePageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';

const timeline = [
  { year: '2022', title: '平台起步', desc: '看片软件正式上线，首批收录 30 余款热门影视播放工具。' },
  { year: '2023', title: '全面迭代', desc: '上线多维筛选与搜索功能，建立软件收录与人工审核标准。' },
  { year: '2024', title: '多端覆盖', desc: '分类扩展至 Windows、安卓、Mac、iOS 与网页端，覆盖主流平台。' },
  { year: '2026', title: '体验升级', desc: '推出移动端适配与全新界面，让影音爱好者随时随地获取工具。' },
];

const values = [
  { icon: 'ri-focus-3-line', title: '专注影视工具', desc: '只收录与影视播放相关的软件，内容垂直、不掺杂无关资源。' },
  { icon: 'ri-user-star-line', title: '人工审核', desc: '每款软件均人工核对版本与来源，拒绝捆绑与恶意程序。' },
  { icon: 'ri-speed-up-line', title: '高效获取', desc: '以清晰的分类、筛选与检索，帮助用户快速找到合适的播放器。' },
];

export default function AboutPage() {
  usePageMeta(
    '关于我们 - 看片软件',
    '了解「看片软件」的网站定位、发展历程与团队理念，我们专注于影视播放类软件的推荐与下载服务。',
  );

  return (
    <div>
      <section className="relative h-[280px] w-full overflow-hidden md:h-[340px]">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20abstract%20banner%20of%20floating%20glowing%20app%20interface%20screens%20and%20media%20windows%20in%20deep%20blue%20space%20with%20warm%20amber%20light%20accents%2C%20clean%20premium%20technology%20mood%2C%20wide%20composition%2C%20no%20text&width=1600&height=600&seq=about-hero&orientation=landscape"
          alt="看片软件 - 影视播放软件下载平台"
          title="关于看片软件"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/75 to-primary-950/40"></div>
        <div className="relative mx-auto flex h-full max-w-[1280px] flex-col justify-center px-4 md:px-6">
          <h1 className="text-3xl font-extrabold text-white md:text-4xl">关于看片软件</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            专注影视播放类软件的推荐与下载，帮助每位影音爱好者轻松找到高速稳定的观影工具。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="mb-8">
          <Breadcrumb items={[{ label: '关于我们' }]} />
        </div>

        <section className="mb-12 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 className="mb-4 text-xl font-bold text-foreground-950 md:text-2xl">平台定位</h2>
            <div className="flex flex-col gap-4 text-sm leading-loose text-foreground-600">
              <p>
                看片软件是一个面向电影电视剧观众与影音发烧友的专业软件下载门户。我们专注于<strong className="text-foreground-900">影视播放类软件</strong>的推荐与下载服务，
                汇聚 Windows 播放器、安卓看片应用、Mac 与 iOS 播放工具，以及解码器与辅助工具等多种类型。
              </p>
              <p>
                我们深知，一款稳定流畅的播放器直接影响观影体验。因此平台不仅展示软件的基本信息，还从功能特色、
                适用平台、版本更新等维度进行结构化整理，让用户在下载前就能全面了解软件是否适合自己。
              </p>
              <p>
                平台所有内容均为虚构演示数据，下载行为仅作界面演示，不涉及真实文件传输，也不提供在线播放、会员登录或支付功能。
              </p>
            </div>
          </div>
          <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 md:h-80">
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20dark%20home%20cinema%20setup%20with%20a%20large%20screen%20glowing%20in%20warm%20amber%20light%20and%20deep%20blue%20ambient%20glow%2C%20comfortable%20sofa%20and%20surround%20speakers%2C%20premium%20editorial%20photography%20mood%2C%20no%20text&width=1000&height=800&seq=about-team&orientation=landscape"
              alt="家庭影院与影视播放场景"
              title="看片软件 - 观影场景"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </section>

        <section className="mb-12">
          <h2 className="mb-6 text-xl font-bold text-foreground-950 md:text-2xl">我们的坚持</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className={`${v.icon} text-2xl`}></i>
                </span>
                <h3 className="mb-2 text-base font-semibold text-foreground-900">{v.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-500">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="mb-6 text-xl font-bold text-foreground-950 md:text-2xl">发展历程</h2>
          <ol className="relative border-l border-background-300 pl-6">
            {timeline.map((t) => (
              <li key={t.year} className="mb-7 last:mb-0">
                <span className="absolute -left-[7px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-accent-500"></span>
                <span className="mb-1 flex items-center gap-3">
                  <span className="rounded-md bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-800">{t.year}</span>
                  <span className="text-base font-semibold text-foreground-900">{t.title}</span>
                </span>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{t.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-error-warning-line text-accent-600"></i>免责声明
            </h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed text-foreground-500">
              <p>本站为影视播放软件的信息展示与推荐平台，所有软件介绍、评分、下载量等数据均为演示用途，不代表真实产品表现。</p>
              <p>本站不提供任何影视内容的在线播放、存储或分发服务，用户应通过合法渠道获取影视资源，并遵守相关法律法规。</p>
              <p>软件版权归各自开发者所有，本站仅作信息整理与导航，若涉及版权问题请通过联系方式与我们沟通。</p>
            </div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-copyright-line text-primary-600"></i>版权声明
            </h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed text-foreground-500">
              <p>本站页面设计、文字组织与信息结构归看片软件所有，未经许可不得用于商业用途。</p>
              <p>站内涉及的软件名称、图标及商标归其各自权利人所有，仅用于识别与说明用途。</p>
              <p>本演示站点内容均为虚构，如有雷同纯属巧合。 © 2026 看片软件 保留所有权利。</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}